import Alpine from 'alpinejs';
import collapse from '@alpinejs/collapse';
import Prism from 'prismjs';
import 'prism-themes/themes/prism-duotone-earth.css';

Alpine.data('app', () => ({
    init() {
        // Default to dark mode unless the user explicitly switched it off.
        if (localStorage.getItem('darkMode') !== 'false') {
            document.documentElement.classList.add('dark');
        }
    }
}));

Alpine.data('shell', () => ({
    // Default to dark; only light when the user has explicitly turned it off.
    dark: localStorage.getItem('darkMode') !== 'false',
    sidebarOpen: false,
    toggleDark() {
        this.dark = !this.dark;
        document.documentElement.classList.toggle('dark', this.dark);
        localStorage.setItem('darkMode', String(this.dark));
    }
}));

Alpine.data('viewportBadge', () => ({
    width: 0,
    height: 0,
    breakpoint: 'xxs',
    init() {
        this.update();
    },
    update() {
        this.width = window.innerWidth;
        this.height = window.innerHeight;
        this.breakpoint = this.getBreakpoint(window.innerWidth);
    },
    getBreakpoint(width: number) {
        const breakpoints = [
            { label: '7xl', min: 4920 },
            { label: '6xl', min: 3680 },
            { label: '5xl', min: 3080 },
            { label: '4xl', min: 2450 },
            { label: '3xl', min: 1840 },
            { label: '2xl', min: 1536 },
            { label: 'xl', min: 1280 },
            { label: 'lg', min: 1024 },
            { label: 'md', min: 768 },
            { label: 'sm', min: 640 },
            { label: 'xs', min: 480 },
            { label: 'xxs', min: 360 }
        ];

        return breakpoints.find(({ min }) => width >= min)?.label ?? 'xxs';
    }
}));

Alpine.data('scrollTopControl', () => ({
    scrollToTop() {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }
}));

Alpine.data('invoiceTotals', () => ({
    rate: '' as string | number,
    assets: 0.00,
    licensing: 0.00,
    hours: 0,
    init() {
        // Sum the per-commit hour values rendered in the timeline above.
        this.hours = Array.from(document.querySelectorAll('.commit-calc dd'))
            .reduce((sum, el) => sum + (parseFloat(el.textContent ?? '') || 0), 0);
    },
    get labor(): number {
        return this.hours * (parseFloat(String(this.rate)) || 0);
    },
    get balance(): number {
        return this.assets + this.licensing + this.labor;
    },
    fmt(n: number): string {
        return (Number(n) || 0).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    }
}));

Alpine.data('componentCard', () => ({
    copied: false,
    showSource: false,
    toggleSource() {
        this.showSource = !this.showSource;
        if (this.showSource) {
            // Highlight after x-collapse reveals the block
            this.$nextTick(() => {
                const pre = (this.$el as HTMLElement).querySelector('pre.language-markup');
                if (pre) Prism.highlightElement(pre.querySelector('code') as Element);
            });
        }
    },
    copy(snippetEl: HTMLElement) {
        // <template> elements store content in a DocumentFragment — innerHTML
        // serialises it back to the original HTML string (no entities mangling,
        // no live DOM side-effects from duplicate IDs).
        const html = (snippetEl instanceof HTMLTemplateElement)
            ? snippetEl.innerHTML.trim()
            : snippetEl.textContent?.trim() ?? '';
        navigator.clipboard.writeText(html);
        this.copied = true;
        setTimeout(() => { this.copied = false; }, 2000);
    }
}));

Alpine.data('paletteCard', () => ({
    copied: false,
    copyText: '',
    copy(text: string) {
        navigator.clipboard.writeText(text);
        this.copyText = text;
        this.copied = true;
        setTimeout(() => { this.copied = false; }, 1500);
    }
}));

window.Alpine = Alpine;
Alpine.plugin(collapse);
Alpine.start();

